import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
  CircularProgress,
  Paper,
  Typography,
  Button,
  Alert,
  Link,
  LinearProgress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import AuthContext from '../../context/AuthContext';
import { whatsappAccountService, userService, billingService, invoiceService } from '../../services/api';
import { daysOverdue, daysUntilDue } from '../../utils/overdue';
import AppLayout from '../layout/AppLayout';
import OnboardingWizard from '../layout/OnboardingWizard';
import PlanRevenueChart, { usePlanRevenue, currentPlanRevenue } from './PlanRevenueChart';
import YourAppCard from './YourAppCard';
import HostingCostCard from './HostingCostCard';
import {
  INK,
  MUTED,
  RULE,
  categoryMeta,
  formatCount,
  formatINR,
  formatPercent,
  Section,
  FigureStrip,
  CompositionBar
} from '../analytics/analyticsUi';

const QUALITY = {
  GREEN: { label: 'Quality high', color: '#1BAA52' },
  YELLOW: { label: 'Quality medium', color: '#C77700' },
  RED: { label: 'Quality low', color: '#C2410C' }
};

const PLAN_NAMES = { basic: 'Basic', standard: 'Standard', premium: 'Premium', enterprise: 'Enterprise' };
const CYCLE_NAMES = { monthly: 'month', quarterly: 'quarter', annually: 'year' };

const greeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
};

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '';

// Health of a number, most urgent first
const accountHealth = (account, summary) => {
  if (summary?.tokenExpired) return { label: 'Reconnect needed', color: '#C2410C', action: true };
  if (account.status === 'suspended') return { label: 'Suspended', color: '#C2410C' };
  if (account.status === 'pending') return { label: 'Pending setup', color: '#C77700' };
  if (!summary) return { label: 'No analytics yet', color: MUTED };
  if (summary.error) return { label: 'Analytics unavailable', color: MUTED };
  return QUALITY[account.qualityRating] || { label: 'Connected', color: '#1BAA52' };
};

const StatusDot = ({ color, label }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}>
    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
    <Typography variant="body2" sx={{ color: INK }}>{label}</Typography>
  </Box>
);

// ---------------------------------------------------------------------------
// This month: the one prominent block on the page
// ---------------------------------------------------------------------------
const MonthPanel = ({ summary, categoryTitle = 'What you sent', partnerNote = 'Your numbers are billed through a partner, so Meta does not share cost.' }) => {
  const sent = summary.messages?.sent || 0;
  const delivered = summary.messages?.delivered || 0;
  const deliveryRate = summary.messages?.deliveryRate || 0;
  const estimatedAccounts = summary.pricing?.estimatedAccounts || 0;
  // Partner-billed numbers without an estimate (rates not loaded) still have no cost
  const costUnavailable = Math.max(0, (summary.pricing?.costUnavailableAccounts || 0) - estimatedAccounts);
  const allCostUnavailable = costUnavailable > 0 && costUnavailable >= summary.totalAccounts;
  const isEstimate = estimatedAccounts > 0;
  const showMargin = summary.pricing?.metaCost !== undefined && isEstimate;
  const missingRates = summary.pricing?.missingRates || [];

  const categories = Object.entries(summary.conversations?.categories || {})
    .map(([key, value]) => ({
      key,
      label: categoryMeta(key).label,
      color: categoryMeta(key).color,
      value: value.count || 0,
      cost: value.cost || 0
    }))
    .filter((c) => c.value > 0);

  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 3,
        borderColor: RULE,
        borderRadius: 2,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'minmax(260px, 5fr) 7fr' },
        overflow: 'hidden'
      }}
    >
      {/* Delivered + cost */}
      <Box
        sx={{
          p: { xs: 2.5, sm: 3.5 },
          borderRight: { md: `1px solid ${RULE}` },
          borderBottom: { xs: `1px solid ${RULE}`, md: 'none' },
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <Typography variant="body2" sx={{ color: MUTED }}>Messages delivered</Typography>
        <Typography
          sx={{
            fontSize: { xs: '2.75rem', md: '3.5rem' },
            fontWeight: 700,
            letterSpacing: '-0.03em',
            lineHeight: 1.05,
            color: INK,
            fontVariantNumeric: 'tabular-nums',
            mt: 0.5
          }}
        >
          {formatCount(delivered)}
        </Typography>

        <Box sx={{ mt: 2 }}>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, deliveryRate)}
            aria-label={`Delivery rate ${formatPercent(deliveryRate)}`}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: RULE,
              '& .MuiLinearProgress-bar': { bgcolor: 'primary.main', borderRadius: 4 }
            }}
          />
          <Typography variant="body2" sx={{ color: MUTED, mt: 1, fontVariantNumeric: 'tabular-nums' }}>
            {formatPercent(deliveryRate)} of {formatCount(sent)} sent
          </Typography>
        </Box>

        <Box sx={{ mt: 'auto', pt: 3 }}>
          <Typography variant="body2" sx={{ color: MUTED }}>{isEstimate ? 'Estimated cost' : 'Approximate cost'}</Typography>
          <Typography
            sx={{
              fontSize: '1.75rem',
              fontWeight: 600,
              letterSpacing: '-0.02em',
              color: allCostUnavailable ? MUTED : INK,
              fontVariantNumeric: 'tabular-nums'
            }}
          >
            {allCostUnavailable ? 'Not available' : formatINR(summary.pricing?.totalCost)}
          </Typography>
          {costUnavailable > 0 && (
            <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 0.5, maxWidth: '40ch' }}>
              {allCostUnavailable
                ? partnerNote
                : `Excludes ${costUnavailable} partner-billed ${costUnavailable === 1 ? 'number' : 'numbers'}.`}
            </Typography>
          )}
          {isEstimate && (
            <Typography variant="caption" sx={{ color: MUTED, display: 'block', mt: 0.5, maxWidth: '42ch' }}>
              Includes an estimate for {estimatedAccounts} partner-billed {estimatedAccounts === 1 ? 'number' : 'numbers'} at message rates.
            </Typography>
          )}
          {showMargin && (
            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${RULE}`, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, fontVariantNumeric: 'tabular-nums' }}>
              <Box>
                <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>Meta's cost</Typography>
                <Typography sx={{ color: INK, fontWeight: 600 }}>{formatINR(summary.pricing.metaCost)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>Your margin</Typography>
                <Typography sx={{ color: summary.pricing.margin < 0 ? '#C2410C' : '#0D7332', fontWeight: 600 }}>{formatINR(summary.pricing.margin)}</Typography>
              </Box>
              {missingRates.length > 0 && (
                <Typography variant="caption" sx={{ color: '#C77700', gridColumn: '1 / -1' }}>
                  Missing rates for {missingRates.length} {missingRates.length === 1 ? 'category' : 'categories'}.{' '}
                  <Link component={RouterLink} to="/admin/rates" sx={{ color: '#C77700' }}>Set rates</Link>
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Category split */}
      <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
        <Typography component="h2" sx={{ fontSize: '1.05rem', fontWeight: 600, color: INK }}>
          {categoryTitle}
        </Typography>
        <Typography variant="body2" sx={{ color: MUTED, mt: 0.5, mb: 2.5 }}>
          Delivered messages by category. Meta charges a different rate for each.
        </Typography>
        {categories.length ? (
          <CompositionBar items={categories} valueLabel="messages" showCost={!allCostUnavailable} />
        ) : (
          <Typography variant="body2" sx={{ color: MUTED }}>
            No billable messages yet this month.
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Numbers table
// ---------------------------------------------------------------------------
const INITIAL_ROWS = 10;

const NumbersSection = ({ accounts, summaryById, navigate, showOwner = false, title, description }) => {
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(false);

  const rows = useMemo(() => accounts
    .map((account) => {
      const summary = summaryById[account._id];
      return { account, summary, health: accountHealth(account, summary) };
    })
    .filter((row) => filter === 'all' || ['#C2410C', '#C77700'].includes(row.health.color))
    .sort((a, b) => (b.summary?.messages?.delivered || 0) - (a.summary?.messages?.delivered || 0)),
  [accounts, summaryById, filter]);

  const attentionCount = useMemo(
    () => accounts.filter((a) => ['#C2410C', '#C77700'].includes(accountHealth(a, summaryById[a._id]).color)).length,
    [accounts, summaryById]
  );

  const visible = expanded ? rows : rows.slice(0, INITIAL_ROWS);
  const columns = ['Number', ...(showOwner ? ['Customer'] : []), 'Sent', 'Delivered', 'Cost', 'Status'];
  const numericColumns = ['Sent', 'Delivered', 'Cost'];

  return (
    <Section
      title={title}
      description={description}
      action={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {attentionCount > 0 && (
            <ToggleButtonGroup size="small" exclusive value={filter} onChange={(e, v) => v && setFilter(v)} aria-label="Filter numbers">
              <ToggleButton value="all" sx={{ textTransform: 'none', px: 1.25 }}>All</ToggleButton>
              <ToggleButton value="attention" sx={{ textTransform: 'none', px: 1.25 }}>
                Needs attention ({attentionCount})
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          <Button size="small" component={RouterLink} to="/whatsapp-accounts" sx={{ textTransform: 'none', display: { xs: 'none', sm: 'inline-flex' } }}>
            Manage numbers
          </Button>
        </Box>
      }
    >
      <TableContainer sx={{ mx: { xs: -2, sm: -3 }, width: 'auto' }}>
        <Table size="small" sx={{ minWidth: showOwner ? 760 : 640 }}>
          <TableHead>
            <TableRow>
              {columns.map((label, i) => (
                <TableCell
                  key={label}
                  align={numericColumns.includes(label) ? 'right' : 'left'}
                  sx={{ color: MUTED, fontWeight: 500, borderColor: RULE, pl: i === 0 ? { xs: 2, sm: 3 } : undefined }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map(({ account, summary, health }) => {
              const sent = summary?.messages?.sent || 0;
              const delivered = summary?.messages?.delivered || 0;
              const owner = account.user && typeof account.user === 'object' ? account.user : summary?.owner;
              return (
                <TableRow
                  key={account._id}
                  hover
                  onClick={() => navigate(`/whatsapp-analytics/${account._id}`)}
                  sx={{ cursor: 'pointer', '& td': { borderColor: RULE, fontVariantNumeric: 'tabular-nums' } }}
                >
                  <TableCell sx={{ pl: { xs: 2, sm: 3 }, py: 1.5 }}>
                    <Link
                      component={RouterLink}
                      to={`/whatsapp-analytics/${account._id}`}
                      underline="hover"
                      onClick={(e) => e.stopPropagation()}
                      sx={{ color: INK, fontWeight: 600, display: 'block' }}
                    >
                      {account.verifiedName || account.businessName}
                    </Link>
                    <Typography variant="body2" sx={{ color: MUTED }}>{account.phoneNumber}</Typography>
                  </TableCell>
                  {showOwner && (
                    <TableCell>
                      <Typography variant="body2" sx={{ color: INK }}>{owner?.company || owner?.name || '–'}</Typography>
                      {owner?.company && owner?.name && (
                        <Typography variant="caption" sx={{ color: MUTED }}>{owner.name}</Typography>
                      )}
                    </TableCell>
                  )}
                  <TableCell align="right">{summary ? formatCount(sent) : '–'}</TableCell>
                  <TableCell align="right">
                    {summary ? (
                      <>
                        {formatCount(delivered)}
                        {sent > 0 && (
                          <Box component="span" sx={{ color: MUTED, ml: 1 }}>{formatPercent((delivered / sent) * 100)}</Box>
                        )}
                      </>
                    ) : '–'}
                  </TableCell>
                  <TableCell align="right">
                    {!summary ? '–' : summary.costEstimated ? (
                      <Tooltip title={summary.metaCost !== undefined ? `Estimated at message rates. Meta's cost ${formatINR(summary.metaCost)}` : 'Estimated at message rates'}>
                        <Box component="span">
                          {formatINR(summary.conversations?.cost)}
                          <Box component="span" sx={{ color: MUTED, ml: 0.5 }}>est.</Box>
                        </Box>
                      </Tooltip>
                    ) : summary.costAvailable === false
                      ? <Box component="span" sx={{ color: MUTED }}>Via partner</Box>
                      : formatINR(summary.conversations?.cost)}
                  </TableCell>
                  <TableCell sx={{ pr: { xs: 2, sm: 3 } }}>
                    {health.action ? (
                      <Link
                        component={RouterLink}
                        to="/whatsapp-onboarding"
                        onClick={(e) => e.stopPropagation()}
                        underline="hover"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, color: health.color, fontWeight: 500, fontSize: '0.875rem' }}
                      >
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: health.color }} />
                        {health.label}
                      </Link>
                    ) : (
                      <StatusDot color={health.color} label={health.label} />
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {rows.length > INITIAL_ROWS && (
        <Button size="small" onClick={() => setExpanded((v) => !v)} sx={{ mt: 1.5, textTransform: 'none' }}>
          {expanded ? 'Show fewer' : `Show all ${rows.length} numbers`}
        </Button>
      )}
    </Section>
  );
};

// ---------------------------------------------------------------------------
// Plan
// ---------------------------------------------------------------------------
const PlanSection = ({ billing }) => (
  <Section
    title="Your plan"
    action={
      <Button size="small" component={RouterLink} to="/billing" sx={{ textTransform: 'none', flexShrink: 0 }}>
        {billing ? 'View billing' : 'Choose a plan'}
      </Button>
    }
  >
    {billing ? (
      <Box>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 600, color: INK, letterSpacing: '-0.01em' }}>
          {PLAN_NAMES[billing.plan] || billing.plan}
        </Typography>
        <Typography variant="body2" sx={{ color: MUTED, mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>
          {formatINR(billing.amount)} per {CYCLE_NAMES[billing.billingCycle] || 'cycle'}
          {billing.whatsappAccount?.phoneNumber && ` for ${billing.whatsappAccount.phoneNumber}`}
        </Typography>
        {billing.endDate && (
          <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${RULE}` }}>
            <Typography variant="body2" sx={{ color: MUTED }}>
              {billing.autoRenew === false ? 'Ends on' : 'Renews on'}
            </Typography>
            <Typography sx={{ color: INK, fontWeight: 500 }}>{formatDate(billing.endDate)}</Typography>
          </Box>
        )}
      </Box>
    ) : (
      <Typography variant="body2" sx={{ color: MUTED, maxWidth: '45ch' }}>
        You don't have an active plan. Choose one to keep sending messages through the portal.
      </Typography>
    )}
  </Section>
);

// ---------------------------------------------------------------------------
// Admin: subscriptions renewing soon
// ---------------------------------------------------------------------------
const RenewalsSection = ({ billings }) => (
  <Section
    title="Renewing in the next 14 days"
    action={
      <Button size="small" component={RouterLink} to="/admin/subscriptions" sx={{ textTransform: 'none', flexShrink: 0 }}>
        Subscriptions
      </Button>
    }
  >
    {billings.length ? (
      <Box
        component="ul"
        tabIndex={0}
        aria-label="Subscriptions renewing in the next 14 days"
        sx={{ listStyle: 'none', p: 0, m: 0, maxHeight: 320, overflowY: 'auto', mr: -1, pr: 1 }}
      >
        {billings.map((billing) => (
          <Box
            component="li"
            key={billing._id}
            sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1.25, borderTop: `1px solid ${RULE}` }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ color: INK, fontWeight: 500 }} noWrap>
                {billing.user?.company || billing.user?.name || 'Customer'}
              </Typography>
              <Typography variant="caption" sx={{ color: MUTED }}>
                {PLAN_NAMES[billing.plan] || billing.plan}, {formatINR(billing.amount)}
                {billing.autoRenew === false && ', not renewing'}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: INK, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
              {new Date(billing.endDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
            </Typography>
          </Box>
        ))}
      </Box>
    ) : (
      <Typography variant="body2" sx={{ color: MUTED }}>No subscriptions renew in the next two weeks.</Typography>
    )}
  </Section>
);

const AdminLinks = () => (
  <Section title="Admin tools">
    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
      {[
        { to: '/admin/users', label: 'Users', text: 'Create accounts and change roles' },
        { to: '/admin/subscriptions', label: 'Subscriptions', text: 'Process payments and renewals' },
        { to: '/admin/plans', label: 'Plans', text: 'Edit prices and limits' },
        { to: '/admin/servers', label: 'Servers', text: 'Hosting cost and which clients run where' },
        { to: '/admin/database', label: 'Database backups', text: 'Back up or restore data' },
        { to: '/admin/audit', label: 'Audit log', text: 'Who changed what, last 30 days' }
      ].map((item) => (
        <Box component="li" key={item.to} sx={{ py: 1.25, borderTop: `1px solid ${RULE}` }}>
          <Link component={RouterLink} to={item.to} underline="hover" sx={{ fontWeight: 500 }}>{item.label}</Link>
          <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>{item.text}</Typography>
        </Box>
      ))}
    </Box>
  </Section>
);

// ---------------------------------------------------------------------------
// New customer: a real sequence, so the steps are numbered
// ---------------------------------------------------------------------------
const GetStarted = ({ hasPlan, onLearnMore }) => {
  const steps = [
    {
      title: 'Connect your WhatsApp number',
      text: 'Sign in with the Facebook account that manages your business, then pick or create a WhatsApp Business account.',
      done: false
    },
    {
      title: 'Choose a plan',
      text: 'Pick the plan that matches how many messages you expect to send each month.',
      done: hasPlan
    },
    {
      title: 'Watch your messages here',
      text: 'Delivery, message categories and cost appear on this page as soon as your number starts sending.',
      done: false
    }
  ];

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 4 }, mb: 3, borderColor: RULE, borderRadius: 2 }}>
      <Typography component="h2" sx={{ fontSize: '1.375rem', fontWeight: 700, color: INK, letterSpacing: '-0.01em' }}>
        Get your first number connected
      </Typography>
      <Typography variant="body2" sx={{ color: MUTED, mt: 0.75, mb: 3, maxWidth: '60ch' }}>
        It takes a few minutes. Have your business's display name, legal name and address ready.
      </Typography>

      <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: { xs: 2.5, md: 4 } }}>
        {steps.map((step, index) => (
          <Box component="li" key={step.title} sx={{ display: 'flex', gap: 1.5 }}>
            <Box
              aria-hidden="true"
              sx={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'grid',
                placeItems: 'center',
                fontSize: '0.875rem',
                fontWeight: 600,
                bgcolor: step.done ? 'primary.main' : 'transparent',
                color: step.done ? '#fff' : INK,
                border: step.done ? 'none' : `1.5px solid ${INK}`
              }}
            >
              {step.done ? '✓' : index + 1}
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600, color: INK }}>{step.title}</Typography>
              <Typography variant="body2" sx={{ color: MUTED, mt: 0.5 }}>{step.text}</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 4 }}>
        <Button variant="contained" component={RouterLink} to="/whatsapp-onboarding" startIcon={<AddIcon />} sx={{ textTransform: 'none' }}>
          Connect a number
        </Button>
        <Button onClick={onLearnMore} sx={{ textTransform: 'none' }}>
          See how the portal works
        </Button>
      </Box>
    </Paper>
  );
};

const DashboardSkeleton = () => (
  <Box aria-busy="true">
    <Skeleton variant="rounded" height={260} sx={{ mb: 3 }} />
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
      <Skeleton variant="rounded" height={240} />
      <Skeleton variant="rounded" height={240} />
    </Box>
  </Box>
);

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const minutesAgo = (iso) => {
  if (!iso) return '';
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `${hours === 1 ? '1 hour' : `${hours} hours`}${rest ? ` ${rest} min` : ''} ago`;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';
  const userId = user?._id || user?.id;

  const [showOnboarding, setShowOnboarding] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [billings, setBillings] = useState([]);
  const [userCount, setUserCount] = useState(0);
  const [accountTotal, setAccountTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openInvoices, setOpenInvoices] = useState([]);

  // Customers: unpaid invoices for the overdue / due-soon banner
  useEffect(() => {
    if (!user || isAdmin) return;
    invoiceService.list({ limit: 200 })
      .then((response) => setOpenInvoices((response.data || []).filter((inv) => ['pending', 'failed'].includes(inv.status))))
      .catch(() => setOpenInvoices([]));
  }, [user, isAdmin]);
  const invoiceTotalOf = (inv) => inv.total ?? (inv.amount + (inv.taxAmount || 0) - (inv.discountAmount || 0));
  const overdueInvoices = openInvoices.filter((inv) => daysOverdue(inv) > 0);
  const dueSoonInvoices = openInvoices.filter((inv) => { const d = daysUntilDue(inv); return d !== null && d >= 0 && d <= 3; });
  const paymentBanner = !isAdmin && (overdueInvoices.length > 0 || dueSoonInvoices.length > 0) && (
    <Alert
      severity={overdueInvoices.length ? 'error' : 'info'}
      variant="outlined"
      sx={{ mb: 3 }}
      action={<Button component={RouterLink} to="/billing" color="inherit" size="small" sx={{ textTransform: 'none', fontWeight: 600 }}>View invoices</Button>}
    >
      {overdueInvoices.length
        ? `${overdueInvoices.length === 1 ? 'One invoice is' : `${overdueInvoices.length} invoices are`} overdue, ${formatINR(overdueInvoices.reduce((sum, inv) => sum + invoiceTotalOf(inv), 0))} in total. Please pay at the earliest.`
        : `${dueSoonInvoices.length === 1 ? 'An invoice' : `${dueSoonInvoices.length} invoices`} for ${formatINR(dueSoonInvoices.reduce((sum, inv) => sum + invoiceTotalOf(inv), 0))} ${dueSoonInvoices.length === 1 ? 'is' : 'are'} due in the next few days.`}
    </Alert>
  );

  const fetchSummary = async (refresh = false) => {
    try {
      setSummaryLoading(true);
      const response = await whatsappAccountService.getAnalyticsSummary({ refresh });
      setSummary(response.success ? response.data : { hasAccounts: false });
    } catch (err) {
      setSummary({ hasAccounts: false, error: true });
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const [accountsRes, billingsRes, usersRes] = await Promise.all([
          whatsappAccountService.getAccounts(1, 1000),
          billingService.getBillings(),
          isAdmin ? userService.getUsers({ limit: 1 }) : Promise.resolve(null)
        ]);

        setAccounts(accountsRes.data || []);
        setAccountTotal(accountsRes.total || accountsRes.data?.length || 0);
        setBillings(billingsRes.data || []);
        setUserCount(usersRes?.total || usersRes?.data?.length || 0);

        // Show the welcome wizard only to new customers: no WhatsApp account connected yet,
        // and not already shown in this browser. (user.id after login, user._id after reload)
        const accountCount = accountsRes.total || accountsRes.data?.length || 0;
        const visitedKey = `dashboard_visited_${userId}`;
        if (userId && !isAdmin && accountCount === 0 && !localStorage.getItem(visitedKey)) {
          setShowOnboarding(true);
          localStorage.setItem(visitedKey, 'true');
        }
      } catch (err) {
        setError('Could not load the dashboard. Refresh the page to try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    fetchSummary();
  }, [user, isAdmin, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const summaryById = useMemo(() => {
    const map = {};
    (summary?.accounts || []).forEach((a) => { map[a.id] = a; });
    return map;
  }, [summary]);

  const activeBillings = useMemo(() => billings.filter((b) => b.status === 'active'), [billings]);

  const myActiveBilling = useMemo(() => activeBillings
    .filter((b) => {
      const ownerId = typeof b.user === 'object' ? b.user?._id : b.user;
      return !isAdmin || String(ownerId) === String(userId);
    })
    .sort((a, b) => new Date(b.endDate || 0) - new Date(a.endDate || 0))[0],
  [activeBillings, isAdmin, userId]);

  const renewingSoon = useMemo(() => {
    const now = Date.now();
    const limit = now + 14 * 24 * 60 * 60 * 1000;
    return activeBillings
      .filter((b) => b.endDate && new Date(b.endDate).getTime() >= now && new Date(b.endDate).getTime() <= limit)
      .sort((a, b) => new Date(a.endDate) - new Date(b.endDate));
  }, [activeBillings]);

  const planRevenue = usePlanRevenue(isAdmin);

  const now = new Date();
  const monthName = now.toLocaleDateString('en-IN', { month: 'long' });
  const firstName = (user?.name || '').split(' ')[0];
  const hasNumbers = accounts.length > 0;

  const monthBlock = summaryLoading ? (
    <Skeleton variant="rounded" height={260} sx={{ mb: 3 }} />
  ) : summary?.hasAccounts ? (
    isAdmin ? (
      <MonthPanel
        summary={summary}
        categoryTitle="What customers sent"
        partnerNote="All numbers are billed through a partner, so Meta does not share cost."
      />
    ) : (
      <MonthPanel summary={summary} />
    )
  ) : (
    <Alert severity="info" variant="outlined" sx={{ mb: 3, borderColor: RULE, color: INK }}>
      {summary?.error
        ? 'Monthly analytics could not be loaded from Meta. Try again in a few minutes.'
        : 'Analytics appear once a number finishes setup with Meta.'}
    </Alert>
  );

  const header = (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2, mb: 3 }}>
      <Box>
        <Typography variant="body2" sx={{ color: MUTED }}>
          {greeting()}{firstName ? `, ${firstName}` : ''}
        </Typography>
        <Typography
          component="h1"
          sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 700, letterSpacing: '-0.025em', color: INK, lineHeight: 1.15, mt: 0.5 }}
        >
          {isAdmin
            ? `${monthName} across all customers`
            : hasNumbers || loading ? `${monthName} so far` : 'Welcome to your WhatsApp portal'}
        </Typography>
        {(isAdmin || hasNumbers) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <Typography variant="body2" sx={{ color: MUTED }}>
              1 to {now.getDate()} {monthName}
              {!isAdmin && `, across ${accounts.length} WhatsApp ${accounts.length === 1 ? 'number' : 'numbers'}`}
              {summary?.generatedAt && !summaryLoading && `, Meta data from ${minutesAgo(summary.generatedAt)}`}
            </Typography>
            <Tooltip title="Reload from Meta. Figures are otherwise kept for up to 4 hours.">
              <span>
                <IconButton size="small" onClick={() => fetchSummary(true)} disabled={summaryLoading} aria-label="Reload analytics from Meta">
                  {summaryLoading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        )}
      </Box>
      {isAdmin ? (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button component={RouterLink} to="/admin/subscriptions" sx={{ textTransform: 'none' }}>Subscriptions</Button>
          <Button variant="contained" component={RouterLink} to="/admin/users" sx={{ textTransform: 'none' }}>Manage users</Button>
        </Box>
      ) : hasNumbers && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button component={RouterLink} to="/billing" sx={{ textTransform: 'none' }}>Plan and billing</Button>
          <Button variant="contained" component={RouterLink} to="/whatsapp-onboarding" startIcon={<AddIcon />} sx={{ textTransform: 'none' }}>
            Connect a number
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <AppLayout>
      <Container maxWidth="lg" sx={{ pb: 6 }}>
        {header}

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        {paymentBanner}

        {loading ? (
          <DashboardSkeleton />
        ) : isAdmin ? (
          <>
            <FigureStrip
              figures={[
                { label: 'Users', value: formatCount(userCount) },
                {
                  label: 'WhatsApp numbers',
                  value: formatCount(accountTotal),
                  hint: summary?.tokenExpiredAccounts ? `${summary.tokenExpiredAccounts} need reconnecting` : undefined
                },
                {
                  label: 'Active subscriptions',
                  value: formatCount(activeBillings.length),
                  hint: renewingSoon.length ? `${renewingSoon.length} renew in the next 14 days` : undefined
                },
                {
                  label: 'Plan revenue per month',
                  value: planRevenue.data ? formatINR(currentPlanRevenue(planRevenue.data)) : planRevenue.error ? '–' : '…',
                  hint: `${monthName}, before GST`
                }
              ]}
            />

            {monthBlock}

            <PlanRevenueChart data={planRevenue.data} error={planRevenue.error} />

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 2fr) minmax(280px, 1fr)' }, columnGap: 3, alignItems: 'start' }}>
              <NumbersSection
                accounts={accounts}
                summaryById={summaryById}
                navigate={navigate}
                showOwner
                title="All WhatsApp numbers"
                description="This month's activity for every connected number, busiest first."
              />
              <Box>
                <RenewalsSection billings={renewingSoon} />
                <HostingCostCard />
                <AdminLinks />
              </Box>
            </Box>
          </>
        ) : (
          <>
            {!hasNumbers && <GetStarted hasPlan={!!myActiveBilling} onLearnMore={() => setShowOnboarding(true)} />}
            {hasNumbers && monthBlock}

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', lg: hasNumbers ? 'minmax(0, 2fr) minmax(280px, 1fr)' : '1fr' },
                columnGap: 3,
                alignItems: 'start'
              }}
            >
              {hasNumbers && (
                <NumbersSection
                  accounts={accounts}
                  summaryById={summaryById}
                  navigate={navigate}
                  title="Your WhatsApp numbers"
                  description="This month's activity for each connected number."
                />
              )}
              <Box>
                <YourAppCard />
                <PlanSection billing={myActiveBilling} />
              </Box>
            </Box>
          </>
        )}
      </Container>

      <OnboardingWizard
        open={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onComplete={() => {
          setShowOnboarding(false);
          navigate('/whatsapp-onboarding');
        }}
        userName={user?.name}
      />
    </AppLayout>
  );
};

export default Dashboard;
